/**
 * usePromptHistory — localStorage-backed prompt history with navigation
 *
 * Replaces the in-memory promptHistory/historyIndex/savedDraft state in ChatPanel.
 * Persists across page refreshes. Deduplicates consecutive identical prompts.
 */
import { useRef, useCallback } from 'react'

const STORAGE_PREFIX = 'papan-agent-prompt-history-'
const MAX_ENTRIES = 100

function load(workspaceId: string): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + workspaceId)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((s): s is string => typeof s === 'string').slice(0, MAX_ENTRIES)
  } catch {
    return []
  }
}

function save(workspaceId: string, entries: string[]): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + workspaceId, JSON.stringify(entries))
  } catch { /* ignore quota errors */ }
}

export function usePromptHistory(workspaceId: string) {
  const entriesRef = useRef<string[]>(load(workspaceId))
  const indexRef = useRef(-1)
  const savedDraftRef = useRef('')

  const append = useCallback((text: string) => {
    const entries = entriesRef.current
    // Dedup consecutive identical prompts
    if (entries[0] === text) return
    const next = [text, ...entries].slice(0, MAX_ENTRIES)
    entriesRef.current = next
    save(workspaceId, next)
  }, [workspaceId])

  const navigateUp = useCallback((currentMessage: string): string | null => {
    const entries = entriesRef.current
    if (entries.length === 0) return null

    if (indexRef.current === -1) {
      savedDraftRef.current = currentMessage
    }

    const newIndex = Math.min(indexRef.current + 1, entries.length - 1)
    indexRef.current = newIndex
    return entries[newIndex] ?? null
  }, [])

  const navigateDown = useCallback((): string | null => {
    if (indexRef.current < 0) return null

    const newIndex = indexRef.current - 1
    indexRef.current = newIndex

    if (newIndex < 0) {
      return savedDraftRef.current
    }
    return entriesRef.current[newIndex] ?? null
  }, [])

  const resetNavigation = useCallback(() => {
    indexRef.current = -1
    savedDraftRef.current = ''
  }, [])

  return {
    entries: entriesRef.current,
    index: indexRef.current,
    savedDraft: savedDraftRef.current,
    append,
    navigateUp,
    navigateDown,
    resetNavigation,
  }
}
